import type React from 'react'
import {memo} from 'react'
import {CircleX} from 'lucide-react'
import Button from '@/components/GDS/molecules/Button/Button'
import styles from './KPIField.module.css'

export type FieldChangeHandler = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | {target: {name: string; value: any}},
  valueWithPrefixSuffix?: string,
) => void

export type KPIFieldType = 'unit' | 'counter'

export interface KPIFieldProps {
  /** Field name, forwarded on the change event. */
  name: string
  /** Control to render: unit-suffixed input or `-`/`+` counter. */
  type?: KPIFieldType
  /** Visible label. */
  label?: string
  /** Current value. */
  value?: string | number
  /** Unit suffix (e.g. '%', 'Hrs'). Optional in both modes. */
  unit?: string
  /** Error message; renders below and reddens the border. */
  error?: string
  placeholder?: string
  disabled?: boolean
  onChange: FieldChangeHandler
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  /** 'unit' only — false allows any chars (default: decimal-only). */
  numericOnly?: boolean
  /** 'counter' only — step amount (default 1). */
  step?: number
  /** 'counter' only — lower bound (default 0). */
  min?: number
  /** 'counter' only — upper bound (optional). */
  max?: number
  /** Text scale: 'sm' = 12px, 'md' = 14px (default). */
  size?: 'sm' | 'md'
  /** Container width (default 304px). */
  width?: number | string
}

const sanitizeDecimalValue = (value: string): string => {
  const sanitized = value.replace(/[^\d.]/g, '')
  const [integerPart, ...fractionParts] = sanitized.split('.')
  if (fractionParts.length === 0) return integerPart
  return `${integerPart}.${fractionParts.join('')}`
}

const sanitizeIntegerValue = (value: string): string => value.replace(/\D/g, '')

/**
 * Reusable KPI entry control.
 * - `type="unit"` (default): input with optional unit suffix box.
 * - `type="counter"`: `-`/`+` stepper, integers clamped to [min, max].
 * Both emit a uniform `{target: {name, value}}` change event.
 */
const KPIField: React.FC<KPIFieldProps> = memo(
  ({
    name,
    type = 'unit',
    label,
    value,
    unit,
    error,
    placeholder,
    disabled,
    onChange,
    onBlur,
    numericOnly = true,
    step = 1,
    min = 0,
    max,
    size = 'md',
    width = 304,
  }) => {
    const textSize = size === 'sm' ? 'text-[12px] leading-[16px]' : 'text-sm leading-5'
    const inputRowClass = error ? `${styles.inputRow} ${styles.inputRowError}` : styles.inputRow
    const dividerClass = error
      ? `${styles.stepperDivider} ${styles.stepperDividerError}`
      : `${styles.stepperDivider} ${styles.stepperDividerNormal}`
    const stepperDivider = <div className={dividerClass} />

    const renderLabel = () =>
      label && (
        <div className={`label-element ${styles.labelElement}`} data-testid="label">
          <span className={`label-text ${styles.labelText}`}>
            <label htmlFor={name}>{label}</label>
          </span>
        </div>
      )

    const renderError = () => error && <span className={styles.errorText}>{error}</span>

    // Counter mode
    if (type === 'counter') {
      const clamp = (n: number) => {
        const lower = Math.max(min, n)
        return typeof max === 'number' ? Math.min(max, lower) : lower
      }
      const emit = (next: number) => onChange({target: {name, value: String(next)}})
      const handleStep = (delta: number) => emit(clamp(Number(value || 0) + delta))
      const handleChange = (raw: string) => onChange({target: {name, value: sanitizeIntegerValue(raw)}})

      return (
        <div className={styles.kpiField} style={{width}}>
          {renderLabel()}
          <div className={inputRowClass} style={{width, minWidth: 0}}>
            <Button
              type="button"
              variant="secondary"
              size="small"
              text="-"
              aria-label={`decrease ${label || name}`}
              onClick={() => handleStep(-step)}
              disabled={disabled}
              className={`${styles.stepperButton} ${textSize}`}
            />
            {stepperDivider}
            <input
              id={name}
              name={name}
              value={(value as any) ?? ''}
              placeholder={placeholder ?? '0'}
              onChange={e => handleChange(e.target.value)}
              onBlur={onBlur}
              disabled={disabled}
              aria-invalid={!!error}
              className={`${styles.input} ${styles.inputCenter} ${textSize}`}
            />
            {unit && <div className={`${styles.unitSuffix} ${textSize}`}>{unit}</div>}
            {stepperDivider}
            <Button
              type="button"
              variant="secondary"
              size="small"
              text="+"
              aria-label={`increase ${label || name}`}
              onClick={() => handleStep(step)}
              disabled={disabled}
              className={`${styles.stepperButton} ${textSize}`}
            />
          </div>
          {renderError()}
        </div>
      )
    }

    // Unit mode
    const handleChange = (raw: string) => {
      const next = numericOnly ? sanitizeDecimalValue(raw) : raw
      onChange({target: {name, value: next}})
    }

    // Show the clear (✕) affordance only when there's a value to clear.
    const showClear = !disabled && value != null && String(value) !== ''

    return (
      <div className={styles.kpiField} style={{width}}>
        {renderLabel()}
        <div className={inputRowClass} style={{width, minWidth: 0}}>
          <div className={styles.inputContainer}>
            <input
              id={name}
              name={name}
              value={value as any}
              placeholder={placeholder ?? 'Enter value'}
              onChange={e => handleChange(e.target.value)}
              onBlur={onBlur}
              disabled={disabled}
              aria-invalid={!!error}
              className={`${styles.input} ${styles.inputTransparent} ${textSize}`}
            />
            {showClear && (
              <button
                type="button"
                aria-label={`clear ${label || name}`}
                // Prevent the input from losing focus before the clear fires.
                onMouseDown={e => e.preventDefault()}
                onClick={() => onChange({target: {name, value: ''}})}
                className={styles.clearButton}
              >
                <CircleX
                  size={16}
                  color="var(--color-interaction-form-background,#FFFFFF)"
                  fill="var(--color-icon-default,#2D2D2D)"
                  strokeWidth={2}
                />
              </button>
            )}
          </div>
          {unit && <div className={`${styles.unitSuffix} ${styles.unitSuffixFixed}`}>{unit}</div>}
        </div>
        {renderError()}
      </div>
    )
  },
)

KPIField.displayName = 'KPIField'

export default KPIField
